<template>
  <div>
    <div class="label-clear-container">
      <div class="label">{{ label }}</div>
      <button id="clear" class="clear" @click="handleClear">
        {{ t("SHARED.CLEAR") }}
      </button>
    </div>
    <div class="signature-pad-container">
      <div v-show="!text" class="type-here">{{ t("SHARED.TYPE_HERE") }}</div>
      <div class="row">
        <div class="col-12 pb-2">
          <input
            id="text-signature-input"
            type="text"
            maxlength="40"
            class="text-signature-input"
            v-model.trim="text"
            @input="handleInput"
            spellcheck="false"
            autocomplete="off"
            ref="inputRef"
          />
        </div>
      </div>
    </div>
    <canvas id="canvas" class="text-signature-canvas" height="240" />
  </div>
</template>
<script setup lang="ts">
import { onBeforeMount, onMounted, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
const props = defineProps({
  label: {
    type: String,
    default: "",
  },
  modelValue: {
    type: String,
    default: "",
  },
});
const emit = defineEmits(["clear", "eSign", "update:modelValue"]);
const { t } = useI18n();
const canvas = ref<HTMLCanvasElement | null>(null);
const canvasWidth = ref();
const context = ref();
const inputRef = ref<HTMLInputElement | null>(null);
const INITIAL_FONT_SIZE = 74;
const currentFontSize = ref(INITIAL_FONT_SIZE);
const text = ref(props.modelValue?.trim().slice(0, 40) || "");

const handleClear = () => {
  text.value = "";
  context.value?.clearRect(0, 0, canvas.value?.width, canvas.value?.height);
  emit("clear");
};

const handleInput = () => {
  canvas.value?.removeAttribute("hidden");
  if (!text.value?.trimStart()) text.value = "";
  canvasWidth.value = canvas.value?.offsetWidth;
  canvas.value?.setAttribute("width", canvasWidth.value);
  context.value = canvas.value?.getContext("2d") as CanvasRenderingContext2D;
  resizeFontToFitPad();
  createSignatureImage();
  canvas.value?.setAttribute("hidden", "hidden");
};

const createSignatureImage = () => {
  if (!canvas.value || !context.value) {
    return;
  }
  context.value?.clearRect(0, 0, canvas.value.width, canvas.value.height);
  context.value.font = `calc(${currentFontSize.value}px + 1vw) Snell Roundhand, MonteCarlo, cursive`;
  context.value.fillStyle = "black";
  context.value.textAlign = "center";
  context.value?.fillText(
    text.value,
    canvas.value.width * 0.5,
    canvas.value.height * 0.5 + 8
  );
  const data = text.value ? canvas.value?.toDataURL() : "";
  emit("eSign", data);
};

const resizeFontToFitPad = () => {
  while (isOverflowing(inputRef.value)) {
    currentFontSize.value--;
    setInputFontSize(currentFontSize.value);
  }
  while (
    !isOverflowing(inputRef.value) &&
    currentFontSize.value < INITIAL_FONT_SIZE
  ) {
    currentFontSize.value++;
    setInputFontSize(currentFontSize.value);
  }
  currentFontSize.value = Math.min(
    INITIAL_FONT_SIZE,
    currentFontSize.value - 2
  );
  setInputFontSize(currentFontSize.value);
};

const isOverflowing = (element) => {
  return element.scrollWidth > element.clientWidth;
};

const setInputFontSize = (size: number) => {
  inputRef.value?.style.setProperty("font-size", `calc(${size}px + 1vw)`);
};

watch(text, (value: string) => {
  text.value = value;
  emit("update:modelValue", text.value);
});

const addLink = (href: string) => {
  const link = document.createElement("link");
  link.href = href;
  link.rel = "preload";
  link.as = "font";
  link.type = "font/woff2";
  document.head.appendChild(link);
};

onBeforeMount(() => {
  addLink("fonts/montecarlo/MonteCarlo-Regular.woff");
  addLink("fonts/montecarlo/MonteCarlo-Regular.woff2");
  addLink("fonts/montecarlo/MonteCarlo-Regular.ttf");
  addLink("fonts/montecarlo/MonteCarlo-Regular.eot");
  addLink("fonts/montecarlo/MonteCarlo-Regular.eot?#iefix");
});

onMounted(() => {
  canvas.value = document.getElementById("canvas") as HTMLCanvasElement;
  text.value && handleInput();
  canvas.value?.setAttribute("hidden", "hidden");
  window.addEventListener("resize", handleInput);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleInput);
});
</script>
<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
.label-clear-container {
  display: flex;
}
.label {
  margin-top: 4px;
  margin-left: 14px;
  font-size: 16px;
  font-weight: bold;
}
.clear {
  margin-top: 4px;
  margin-left: auto;
  padding-right: 14px;
  position: relative;
  font-size: 16px;
  text-decoration: none;
  color: $pj-color-green;
  font-weight: 600;
  background: none;
  border: none;
  width: fit-content;
}
.clear:hover {
  font-weight: 700;
}
.text-signature-input {
  background-color: #e0ffe9;
  border: 3px solid $pj-color-green;
  border-radius: 8px;
  width: 100%;
  height: 240px;
  opacity: 0.85;
  font-family: Snell Roundhand, MonteCarlo, cursive;
  font-size: calc(74px + 1vw);
  font-weight: normal;
  text-align: center;
}
.text-signature-canvas {
  width: 100%;
  border: solid 1px green;
}
.signature-pad-container {
  position: relative;
  user-select: none;
}
.signature-pad-container.row {
  position: absolute;
  z-index: 99;
}
.type-here {
  position: relative;
  text-align: center;
  height: 0;
  top: 80px;
  font-weight: 600;
  font-size: 54px;
  color: black;
}
</style>
